import { FC, useEffect, useState } from "react";
import {
    IonAlert,
    IonButton,
    IonCard,
    IonCardContent,
    IonContent,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonLoading,
    IonPage,
    IonText,
} from "@ionic/react";
import { HeaderComponent } from "../../../../components";
import {
    Controller,
    FieldValues,
    FormProvider,
    useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
    syncRefInsightMatches,
    linkRefInsightCreds,
    disconnectRefInsightSubscription,
} from "../../../../services/integrationService";
import { routes } from "../../../../route/constants";
import { refreshSubscriptions } from "../../../../services/subscriptionService";
import { IntegrationPartner, SubscriptionItems } from "refsix-js-models";
import { useSubscription } from "../../../../components/hooks";
import { openOutline, syncOutline } from "ionicons/icons";
import { trackEvent } from "../../../../services/analytics/analyticsService";
import * as Sentry from "@sentry/react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import refsixSquareLogo from "../../../../assets/images/icon/refsix-square-icon.png";
import refInsightSquareLogo from "../../../../assets/images/icon/refinsight-square-icon.png";
import { validateRefInsightCredentials } from "refsix-core";

const RefInsightIntegration: FC = () => {
    const { t } = useTranslation();
    const developerToolsEnabled = useSelector(
        (state: RootState) => state.developerTools.enabled
    );
    const [inputsDisabled, setInputsDisabled] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const subscriptions = useSubscription();
    const [isSubscriptionValid, setIsSubscriptionValid] =
        useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);

    const formMethods = useForm<FieldValues>({
        mode: "all",
        defaultValues: {
            refInsightURL: "",
        },
    });

    const {
        formState: { errors },
        watch,
    } = formMethods;

    const refInsightCreds = watch("refInsightCreds");

    useEffect(() => {
        const refInsightSubscription = subscriptions?.subscriptions.find(
            (subscription: SubscriptionItems) =>
                subscription.provider === "refInsight"
        );
        if (
            refInsightSubscription &&
            new Date(refInsightSubscription.validUntil) > new Date()
        ) {
            setIsSubscriptionValid(true);
            formMethods.setValue(
                "refInsightCreds",
                refInsightSubscription.rawData.creds
            );
            setInputsDisabled(true);
        } else {
            setIsSubscriptionValid(false);
            setInputsDisabled(false);
            formMethods.setValue("refInsightCreds", "");
        }
    }, [formMethods, subscriptions]);

    const linkRefInsight = async () => {
        setErrorMessage(null); // clear previous error message
        setLoading(true);
        // Check if refInsightCreds are valid.
        if (
            !refInsightCreds ||
            !validateRefInsightCredentials(refInsightCreds)
        ) {
            setErrorMessage(
                "Ref Insight credentials invalid. Please check and try again."
            );
            setLoading(false);
            return;
        }
        try {
            await linkRefInsightCreds(refInsightCreds);
            trackEvent("AddIntegrationSuccess", {
                partner: IntegrationPartner.refinsight,
            });
            // The account is now linked; a refresh/sync failure past this point
            // must not be reported as a link failure, so keep both in the inner
            // try (only a genuine link rejection reaches the outer catch).
            try {
                await refreshSubscriptions();
                await syncRefInsightMatches();
                setLoading(false);
                setShowSuccessAlert(true);
                setInputsDisabled(true);
                setIsSubscriptionValid(true);
            } catch (error: any) {
                console.error("Error syncing with Ref Insight:", error);
                if (error.response && error.response.status !== 200) {
                    setErrorMessage(
                        `Failed to sync with Ref Insight. Please check credentials and try again (${error.response.status})`
                    );
                }
                setLoading(false);
            }
        } catch (error: any) {
            console.error("Error while linking Ref Insight:", error);
            trackEvent("AddIntegrationFailed", {
                partner: IntegrationPartner.refinsight,
            });
            // request (axios) rejects on any non-2xx, so invalid credentials
            // surface here rather than as a resolved non-200 response. The
            // backend returns 400/403 for invalid Ref Insight credentials.
            const status = error?.response?.status;
            if (status === 400 || status === 403) {
                setErrorMessage(
                    `Ref Insight credentials invalid. Please check and try again. (${status})`
                );
                setInputsDisabled(false);
                setIsSubscriptionValid(false);
            } else {
                setErrorMessage("Failed to link Ref Insight.");
            }
            setLoading(false);
            return;
        }
    };

    const syncWithRefInsight = async () => {
        setLoading(true);
        const syncMatchesResponse = await syncRefInsightMatches();
        if (!syncMatchesResponse) {
            setErrorMessage("Failed to sync with Ref Insight.");
            setLoading(false);
            return;
        }
        setLoading(false);
    };

    const disconnectRefInsight = async () => {
        setErrorMessage(null);
        setLoading(true);
        const response = await disconnectRefInsightSubscription();
        try {
            if (response.status === 200) {
                trackEvent("RemoveIntegrationSuccess", {
                    partner: IntegrationPartner.refinsight,
                });
                setLoading(false);
                refreshSubscriptions().then(() => {
                    setIsSubscriptionValid(false);
                });
            } else {
                trackEvent("RemoveIntegrationFailed", {
                    partner: IntegrationPartner.refinsight,
                });
                setLoading(false);
                setErrorMessage("Failed to disconnect Ref Insight.");
            }
        } catch (error) {
            console.error("Error while disconnecting Ref Insight:", error);
            Sentry.captureException(error);
            setLoading(false);
            setErrorMessage("Failed to disconnect Ref Insight.");
        }
    };

    return (
        <IonPage>
            <HeaderComponent
                showBackButton
                defaultHref={routes.refsixFixtures}
                title="Ref Insight"
            />
            <IonContent>
                <IonLoading isOpen={loading} />
                <IonAlert
                    isOpen={showSuccessAlert}
                    onDidDismiss={() => setShowSuccessAlert(false)}
                    message={t("integrations.successMessage")}
                    buttons={[t("help.OnboardingVideo.controls.ok")]}
                />
                <div
                    style={{
                        justifyContent: "center",
                        alignItems: "center",
                        display: "flex",
                        paddingTop: "36px",
                        paddingBottom: "21px",
                    }}
                >
                    <img
                        style={{ width: "64px" }}
                        src={refsixSquareLogo}
                        alt={"refsix logo"}
                    />
                    <IonText
                        style={{
                            margin: "0 20px",
                            fontSize: "30px",
                            fontWeight: "700",
                        }}
                    >
                        +
                    </IonText>
                    <img
                        style={{
                            width: "64px",
                            border: "1px solid #fd5930",
                            borderRadius: "12px",
                        }}
                        src={refInsightSquareLogo}
                        // srcSet={refInsightLogo}
                        alt={"refinsight logo"}
                    />
                </div>
                <IonCard style={{ marginBottom: "16px" }}>
                    <IonCardContent>
                        <IonText
                            style={{
                                fontWeight: "bold",
                                fontSize: "24px",
                                color: "white",
                            }}
                        >
                            {t(
                                "integrations.refInsight.integrationDescriptionHeading"
                            )}
                        </IonText>
                        <p style={{ color: "white", marginBottom: "16px" }}>
                            <IonText>
                                {t(
                                    "integrations.refInsight.integrationDescription"
                                )}
                            </IonText>
                        </p>
                        <IonText
                            style={{
                                fontWeight: "bold",
                                fontSize: "24px",
                                color: "white",
                            }}
                        >
                            {t(
                                "integrations.refInsight.integrationInstructionsHeading"
                            )}
                        </IonText>
                        <p style={{ color: "white", marginBottom: "16px" }}>
                            <IonText>
                                {t(
                                    "integrations.refInsight.integrationInstructions"
                                )}
                            </IonText>
                        </p>
                        <IonButton
                            onClick={() => {
                                window.open(
                                    "https://app.refinsight.com/ords/r/refinsight/officials/refsix",
                                    "_system"
                                );
                            }}
                            size={"small"}
                        >
                            <IonIcon slot="start" icon={openOutline} />
                            Ref Insight
                        </IonButton>
                    </IonCardContent>
                </IonCard>
                <IonCard color="">
                    <FormProvider {...formMethods}>
                        <form>
                            <IonItem
                                disabled={inputsDisabled}
                                className={
                                    errors &&
                                    !inputsDisabled &&
                                    errors["refInsightCreds"]
                                        ? "danger"
                                        : ""
                                }
                                lines="none"
                            >
                                <IonLabel>
                                    {t(
                                        "integrations.refInsight.refInsightCreds"
                                    )}
                                </IonLabel>
                                <Controller
                                    rules={{ required: true }}
                                    name="refInsightCreds"
                                    render={({
                                        field: { onBlur, onChange, ...rest },
                                    }) => (
                                        <IonInput
                                            // disabled={inputsDisabled}
                                            onIonChange={onChange}
                                            {...rest}
                                        />
                                    )}
                                />
                            </IonItem>
                        </form>
                    </FormProvider>
                </IonCard>
                <IonCard>
                    {!isSubscriptionValid && (
                        <>
                            <IonButton
                                disabled={inputsDisabled}
                                expand="block"
                                onClick={linkRefInsight}
                                data-testid="button-integration-refinsight-connect"
                            >
                                {t("general.connect")}
                            </IonButton>
                        </>
                    )}
                    {isSubscriptionValid && (
                        <>
                            <IonButton
                                expand="block"
                                data-testid="button-integration-refinsight-disconnect"
                                onClick={disconnectRefInsight}
                            >
                                {t("general.disconnect")}
                            </IonButton>
                            {developerToolsEnabled && (
                                <IonButton
                                    expand="block"
                                    onClick={syncWithRefInsight}
                                    data-testid="button-integration-refInsight-sync"
                                >
                                    <IonIcon
                                        slot="start"
                                        icon={syncOutline}
                                        style={{ marginRight: "8px" }}
                                    />
                                    Sync
                                </IonButton>
                            )}
                        </>
                    )}
                </IonCard>
                {/*<IonCard color="transparent">*/}
                {/*    <IonCardContent>*/}
                {/*        <a*/}
                {/*            href={*/}
                {/*                "https://refsix.zendesk.com/hc/en-us/articles/32860199497233-Integration-with-RefInsight"*/}
                {/*            }*/}
                {/*            target="_system"*/}
                {/*        >*/}
                {/*            {t("integrations.refInsight.learnMore")}*/}
                {/*        </a>*/}
                {/*    </IonCardContent>*/}
                {/*</IonCard>*/}

                {/* Display error message */}
                {errorMessage && (
                    <IonCard color="danger">
                        <IonItem>
                            <IonText color="danger">
                                <p>{errorMessage}</p>
                            </IonText>
                        </IonItem>
                    </IonCard>
                )}
            </IonContent>
        </IonPage>
    );
};

export default RefInsightIntegration;
